import {
  View,
  VStack,
  HStack,
  Divider,
  Input,
  InputField,
  Pressable
} from "@gluestack-ui/themed";
import { Text } from "@repo/core/components/core/Text";
import { Button } from "@repo/core/components/core/Button";
import Scaffolds from "@repo/core/components/layout/Scaffolds";
import { StatusBarVariantEnum } from "@repo/core/components/layout/StatusBar/types";
import { NewTransactionFidelityController } from "./controller";
import { Icon } from "@repo/core/components/core/Icon";
import { ModalTemplate } from "@repo/core/components/layout/ModalTemplate";
import {
  numberToBrl,
  splitCurrencyValue
} from "@repo/core/utils/currency.utils";
import { formatToBRNumber } from "@repo/core/utils/string.utils";

const NewTransactionFidelity = () => {
  const {
    handleBack,
    isMobile,
    handleOnSubmitForm,
    transactionValue,
    discountValue,
    discountPercentage,
    discountedTransactionValue,
    totalBonus,
    usingBonus,
    handleToggleBonus,
    handleRedeem,
    handleSkipRedeem,
    mockFidelityDataInfo,
    mockFidelityOptions,
    modalRef,
    inputBonusValueMasked,
    handleInputChange,
    availableBonus,
    title,
    isFidelityOnly,
    redeemedPoints,
    inputBonusValue
  } = NewTransactionFidelityController();

  const { symbol: totalBonusCurrency, amount: totalBonusValue } =
    splitCurrencyValue(availableBonus || totalBonus);

  return (
    <Scaffolds.Root
      showHeader={false}
      statusBarVariant={StatusBarVariantEnum.STORE_SELECT}
    >
      <Scaffolds.StoreSelect title={title} onBackPress={handleBack}>
        <Scaffolds.Form
          showHeader={false}
          footerContent={
            <View
              w="$full"
              gap="$4"
              flexDirection={!isMobile ? "row-reverse" : "column"}
              pt={isMobile ? "$0" : "$12"}
              px="$4"
              pb="$4"
              justifyContent="flex-end"
            >
              <Button
                flex={isMobile ? undefined : 1}
                size="xl"
                action="secondary"
                text="Continuar"
                onPress={handleOnSubmitForm}
              />
            </View>
          }
        >
          <VStack flex={1}>
            <VStack px="$4" gap="$6">
              <Text>Deseja resgatar pontos e aumentar o bônus disponível?</Text>
              {mockFidelityDataInfo.availablePoints > 0 && (
                <View
                  borderWidth={redeemedPoints ? 0 : 1}
                  borderColor="$borderLight200"
                  borderRadius="$lg"
                  p="$4"
                  bg={redeemedPoints ? "$primary50" : "$backgroundLight0"}
                >
                  <HStack justifyContent="space-between" alignItems="center">
                    <HStack alignItems="center" gap="$2">
                      <Icon
                        name="gift"
                        size={24}
                        color="$primary700"
                        variant="solid"
                      />
                      <VStack>
                        <Text fontWeight={600}>
                          {redeemedPoints
                            ? "Pontos resgatados"
                            : "Saldo de Pontos"}
                        </Text>
                        {!redeemedPoints && (
                          <HStack gap="$1">
                            <Text
                              fontSize="$2xl"
                              fontWeight={600}
                              color="$primary700"
                            >
                              {formatToBRNumber(
                                mockFidelityDataInfo.availablePoints
                              )}
                            </Text>
                            <Text fontSize="$2xl" fontWeight={600}>
                              pts
                            </Text>
                          </HStack>
                        )}
                        {redeemedPoints && (
                          <Text>
                            {formatToBRNumber(redeemedPoints.points)} pts
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                    {redeemedPoints ? (
                      <Pressable onPress={() => modalRef.current?.open()}>
                        <Icon
                          name="chevron-right"
                          size={20}
                          color="$secondary700"
                        />
                      </Pressable>
                    ) : (
                      <Button
                        size="sm"
                        variant="link"
                        action="primary"
                        text="Resgatar"
                        onPress={() => modalRef.current?.open()}
                        textStyle={{
                          color: "$primary700"
                        }}
                      />
                    )}
                  </HStack>
                </View>
              )}

              {totalBonus > 0 && (
                <View
                  backgroundColor="$backgroundLight50"
                  borderRadius="$lg"
                  p="$4"
                  gap="$2"
                >
                  <HStack justifyContent="space-between" alignItems="center">
                    <HStack alignItems="center" gap="$2">
                      <Icon
                        name="tag"
                        size={24}
                        color="$primary700"
                        variant="solid"
                      />
                      <VStack>
                        <Text fontWeight={600}>Bônus disponível</Text>
                        <HStack alignItems="baseline">
                          <Text fontSize="$xl" fontWeight={500}>
                            {totalBonusCurrency}
                          </Text>
                          <Text
                            fontSize="$2xl"
                            fontWeight={500}
                            color="$primary700"
                          >
                            {totalBonusValue}
                          </Text>
                        </HStack>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="link"
                      action={usingBonus ? "negative" : "primary"}
                      text={usingBonus ? "Remover" : "Utilizar"}
                      onPress={handleToggleBonus}
                      textStyle={{
                        color: "$primary700"
                      }}
                    />
                  </HStack>

                  {usingBonus && (
                    <VStack gap="$2">
                      <Text color="$textLight900">Quanto deseja utilizar?</Text>
                      <VStack gap="$1">
                        <Text family="roboto" fontWeight={500} fontSize="$sm">
                          Bônus
                        </Text>
                        <Input
                          borderWidth={0.5}
                          borderColor="$borderLight500"
                          overflow="hidden"
                        >
                          <InputField
                            flex={1}
                            value={inputBonusValueMasked}
                            size="md"
                            onChangeText={handleInputChange}
                            keyboardType="numeric"
                            enterKeyHint="done"
                          />
                        </Input>
                      </VStack>
                      <Text fontSize="$xs" color="$textLight400" mt="$1">
                        O bônus é descontado do valor total da compra.
                      </Text>
                    </VStack>
                  )}
                </View>
              )}
            </VStack>

            <VStack flex={1} justifyContent="flex-end" gap="$2">
              {isMobile && <Divider h="$2" bg="$backgroundDark100" />}
              <HStack
                py={isMobile ? "$0" : "$3"}
                bg={isMobile ? "$backgroundLight0" : "$backgroundLight100"}
                borderRadius={isMobile ? "$none" : "$lg"}
                justifyContent="space-between"
                px="$6"
              >
                <VStack gap="$1">
                  <Text fontWeight={500}>
                    {isFidelityOnly ? "Valor do Resgate:" : "Valor da Compra:"}
                  </Text>
                  <Text fontWeight={600} fontSize="$xl" color="$textLight900">
                    {isFidelityOnly
                      ? numberToBrl(totalBonus)
                      : numberToBrl(discountedTransactionValue)}
                  </Text>
                  {usingBonus &&
                    !isFidelityOnly &&
                    Number(inputBonusValue) > 0 && (
                      <Text strikeThrough>{numberToBrl(transactionValue)}</Text>
                    )}
                </VStack>
                {discountValue > 0 && !isFidelityOnly && (
                  <VStack gap="$1" alignItems="flex-end">
                    <Text fontWeight={500}>Desconto Total:</Text>
                    <HStack alignItems="center" gap="$1">
                      <Icon
                        name="tag"
                        size={20}
                        color="$primary700"
                        variant="solid"
                      />
                      <Text fontWeight={600} fontSize="$xl" color="$primary700">
                        {discountPercentage.toFixed(0)}%
                      </Text>
                    </HStack>
                    <Text>(-{numberToBrl(discountValue)})</Text>
                  </VStack>
                )}
              </HStack>
            </VStack>
          </VStack>
        </Scaffolds.Form>
      </Scaffolds.StoreSelect>
      <ModalTemplate.RedeemPoints
        ref={modalRef}
        pointsList={mockFidelityOptions}
        onRedeem={handleRedeem}
        onSkip={handleSkipRedeem}
      />
    </Scaffolds.Root>
  );
};

export default NewTransactionFidelity;
